import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const MakingItBig: React.FC = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const drawTransition = {
      duration: 1.5,
      ease: "easeInOut" as const
  };

  return (
    <section ref={containerRef} className="py-24 px-4 md:px-8 bg-navy relative overflow-hidden text-white">
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="font-bold text-4xl md:text-6xl mb-6 relative inline-block z-10"
          >
            Growing a game is <br/>
            <span className="relative inline-block">
              {/* Text gets z-10 to stay on top */}
              <span className="relative z-10">tougher than you think.</span>
              {/* SVG gets z-0 to stay behind */}
              <svg className="absolute w-[105%] h-8 -bottom-3 left-[-2.5%] text-accent z-0 pointer-events-none" viewBox="0 0 400 30" preserveAspectRatio="none">
                <motion.path 
                  d="M5,20 Q100,28 200,18 T395,20" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={isInView ? { pathLength: 1 } : {}}
                  transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                />
              </svg>
            </span>
          </motion.h2>
        </div>

        <div className="flex flex-col md:flex-row justify-center gap-8 md:gap-16 items-center">
          {/* Card 1: Great Game, No Players */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative flex flex-col items-center"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1.5 rounded-lg z-10 whitespace-nowrap">
              FAILED?
            </span>
            <div className="w-52 h-52 md:w-64 md:h-64 bg-zinc-900 border border-zinc-700 rounded-3xl flex items-center justify-center relative overflow-hidden">
              <svg width="100" height="130" viewBox="0 0 100 130" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {/* Head */}
                <motion.circle cx="50" cy="25" r="18" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={drawTransition} />
                {/* X Eyes */}
                <motion.path d="M42 22 L46 26 M46 22 L42 26" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.3}} />
                <motion.path d="M54 22 L58 26 M58 22 L54 26" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.3}} />
                {/* Body */}
                <motion.path d="M50 43 L50 85" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.4}} />
                {/* Arms */}
                <motion.path d="M50 55 L30 70" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.5}} />
                <motion.path d="M50 55 L70 70" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.5}} />
                {/* Legs */}
                <motion.path d="M50 85 L35 115" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.6}} />
                <motion.path d="M50 85 L65 115" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.6}} />
              </svg>
            </div>
            <p className="mt-6 text-xl font-bold text-accent">Great Game, No Players</p>
          </motion.div>

          {/* Card 2: Confusing Content Strategy */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="relative flex flex-col items-center"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-bold px-4 py-1.5 rounded-lg z-10 whitespace-nowrap">
              STRATEGY?
            </span>
            <div className="w-52 h-52 md:w-64 md:h-64 bg-zinc-900 border border-zinc-700 rounded-3xl flex items-center justify-center relative overflow-hidden">
              <svg width="140" height="130" viewBox="0 0 140 130" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {/* Question marks */}
                <motion.text x="15" y="45" fontSize="32" fill="hsl(232, 100%, 71%)" stroke="none" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.8, duration: 0.5 }}>?</motion.text>
                <motion.text x="110" y="45" fontSize="32" fill="hsl(232, 100%, 71%)" stroke="none" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 1, duration: 0.5 }}>?</motion.text>
                
                {/* Head */}
                <motion.circle cx="70" cy="25" r="18" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={drawTransition} />
                {/* Neutral face */}
                <motion.circle cx="64" cy="23" r="2" fill="white" stroke="none" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.5 }} />
                <motion.circle cx="76" cy="23" r="2" fill="white" stroke="none" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.5 }} />
                <motion.path d="M65 32 L75 32" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.4}} />
                {/* Body */}
                <motion.path d="M70 43 L70 85" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.4}} />
                {/* Arms raised in confusion */}
                <motion.path d="M70 55 L45 40" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.5}} />
                <motion.path d="M70 55 L95 40" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.5}} />
                {/* Legs */}
                <motion.path d="M70 85 L55 115" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.6}} />
                <motion.path d="M70 85 L85 115" initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1 } : {}} transition={{...drawTransition, delay: 0.6}} />
              </svg>
            </div>
            <p className="mt-6 text-xl font-bold text-accent">Confusing Content Strategy</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MakingItBig;
